import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants/http-header.constants';
import { PlayResultDetail } from '@r1-compass-library/models/reporting/reporting-data.model';
import { AllCharacterHttpParamsEncoder } from '@r1-compass-library/utilities/AllCharacterHttpParamsEncoder';
import { Observable, catchError, of, retry } from 'rxjs';
import { PlayReportingService } from './play-reporting.service';
import { Environment } from '@r1-compass-library/environments/environment';
import {
    AggregateResponse,
    PlayResultDetailsCountsSearch,
    ReportingAggregateRequest,
} from '@r1-compass-library/models';
import { PlayResultDetailsSearch } from '@r1-compass-library/models/search/reporting/play-result-details-search.model';

@Injectable({
    providedIn: 'root',
})
export class PlayResultDetailsService extends PlayReportingService {
    readonly #http = inject(HttpClient);

    private URL = Environment.baseUrl + '/frontend/play-result-details';

    constructor() {
        super();
    }

    listPlayResultDetails(search: PlayResultDetailsSearch): Observable<PlayResultDetail[]> {
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
                fromObject: search,
            }),
        };

        return this.#http.get<PlayResultDetail[]>(this.URL, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<PlayResultDetail[]>();
            })
        );
    }

    getAggregates(request: ReportingAggregateRequest): Observable<AggregateResponse[]> {
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
                fromObject: {
                    operation: request.operation,
                    field: request.field,
                },
            }),
        };

        this.addSearchParams(request.search, customHttpOptions);

        if (request.groupBy) {
            customHttpOptions.params = customHttpOptions.params.append('groupBy', request.groupBy);
        }

        if (request.groupByJoinAttribute) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupByJoinAttribute',
                request.groupByJoinAttribute
            );
        }

        if (request.groupBySecondsRange) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupBySecondsRange',
                request.groupBySecondsRange.toString()
            );
        }

        if (request.groupBySecondsOffset) {
            customHttpOptions.params = customHttpOptions.params.append(
                'groupBySecondsOffset',
                request.groupBySecondsOffset.toString()
            );
        }

        customHttpOptions.params = customHttpOptions.params.delete('search').delete('resultsUrl');

        return this.#http.get<AggregateResponse[]>(`${this.URL}/aggregates`, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<AggregateResponse[]>();
            })
        );
    }

    getJoinedAggregates(search: PlayResultDetailsCountsSearch): Observable<any[]> {
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
            }),
        };

        this.addSearchParams(search, customHttpOptions);

        return this.#http.get<AggregateResponse[]>(`${this.URL}/joined-aggregates`, customHttpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<AggregateResponse[]>();
            })
        );
    }
}
